import { createSelector } from '@reduxjs/toolkit';

import { Account } from 'protocol/api/billing_new/dto_account_new_pb';
import { DiagnocoinBatch } from 'protocol/api/billing_new/dto_diagnocoin_new_pb';
import { Order } from 'protocol/api/billing_new/dto_order_new_pb';
import { ProductStateStatus } from 'protocol/api/billing_new/dto_product_new_pb';
import { ServiceType } from 'protocol/api/billing_new/dto_services_new_pb';

import { LoadingStateType } from '@/shared/config';

import { RootState } from '@/app/model/store';

import {
  getServicesFromSubscriptionList,
  getSubscriptionStartMillis,
  isCurrentSubscription,
  isLimitScheduledSubscription,
  isProductActive,
  isScheduledSubscription,
} from '../lib';

import { BillingStateLoadersKeys } from './billingSlice';

export const selectInventory = (state: RootState) => state.billing.inventory;

const selectAvailableProducts = (state: RootState) => state.billing.products;

export const selectAccountInfo = (state: RootState): Account =>
  state.billing.accountInfo;

export const selectOrders = (state: RootState): Order[] => state.billing.orders;

const selectSubscriptions = createSelector(selectInventory, (inventory) =>
  inventory
    .filter(
      (inventoryItem) =>
        inventoryItem.Status !== ProductStateStatus.ProductStateStatusDeleted &&
        inventoryItem.Model?.Kind.case === 'Subscription',
    )
    .sort((firstSubscription, secondSubscription) => {
      // A limit-scheduled subscription has Start = 0, so sorting by Start alone
      // would put it ahead of everything — including a time-scheduled one, which
      // selectScheduledSubscription would then never return. It has no position
      // on the timeline at all, so it goes last.
      const firstIsLimitScheduled =
        isLimitScheduledSubscription(firstSubscription);
      const secondIsLimitScheduled =
        isLimitScheduledSubscription(secondSubscription);

      if (firstIsLimitScheduled !== secondIsLimitScheduled) {
        return firstIsLimitScheduled ? 1 : -1;
      }

      const firstStart = getSubscriptionStartMillis(firstSubscription);
      const secondStart = getSubscriptionStartMillis(secondSubscription);

      return firstStart - secondStart;
    }),
);

export const selectCurrentSubscription = createSelector(
  selectSubscriptions,
  (subscriptions) => {
    const now = Date.now();

    return subscriptions.find((subscription) =>
      isCurrentSubscription(subscription, now),
    );
  },
);

export const selectScheduledSubscription = createSelector(
  selectSubscriptions,
  (subscriptions) => {
    const now = Date.now();

    return subscriptions.find((subscription) =>
      isScheduledSubscription(subscription, now),
    );
  },
);

export const selectPackages = createSelector(selectInventory, (inventory) =>
  inventory.filter(
    (inventoryItem) =>
      inventoryItem.Status !== ProductStateStatus.ProductStateStatusDeleted &&
      inventoryItem.Model.Kind.case === 'Package',
  ),
);

export const getLoadingStateSelector =
  (loadingKey: BillingStateLoadersKeys) =>
  (state: RootState): LoadingStateType =>
    state.billing.loaders[loadingKey];

export const selectAvailableSubscriptions = createSelector(
  selectAvailableProducts,
  (products) =>
    products.filter((product) => product.Kind.case === 'Subscription'),
);

export const selectAvailablePackages = createSelector(
  selectAvailableProducts,
  (products) => products.filter((product) => product.Kind.case === 'Package'),
);

// Coin packs credit balance rather than granting service leftovers, so they are
// their own product kind and never appear in the package list.
export const selectDiagnocoinAvailable = (state: RootState) =>
  Number(state.billing.diagnocoinWallet?.Available ?? 0);

export const selectDiagnocoinReserved = (state: RootState) =>
  Number(state.billing.diagnocoinWallet?.Reserved ?? 0);

export const selectHasDiagnocoinWallet = (state: RootState) =>
  Boolean(state.billing.diagnocoinWallet?.AccountID);

const selectDiagnocoinBatches = (state: RootState): DiagnocoinBatch[] =>
  state.billing.diagnocoinBatches;

export const selectDiagnocoinBatchesNewestFirst = createSelector(
  selectDiagnocoinBatches,
  (batches) =>
    [...batches].sort(
      (firstBatch, secondBatch) =>
        (secondBatch.PurchasedAt?.toDate().getTime() ?? 0) -
        (firstBatch.PurchasedAt?.toDate().getTime() ?? 0),
    ),
);

export const selectAutoUseDiagnocoins = createSelector(
  selectAccountInfo,
  (account) => Boolean(account?.Settings?.AutoUseDiagnocoins),
);

export const selectAvailableDiagnocoinPacks = createSelector(
  selectAvailableProducts,
  (products) => products.filter((product) => product.Kind.case === 'Diagnocoin'),
);

export const selectOrderToAdd = (state: RootState) => state.billing.orderToAdd;

export const selectActiveProducts = createSelector(selectInventory, (inventory) =>
  inventory.filter(isProductActive),
);

export const selectServicesByType = (serviceType: ServiceType) =>
  createSelector(selectActiveProducts, (activeProducts) =>
    getServicesFromSubscriptionList(activeProducts).filter((service) =>
      service.Types.includes(serviceType),
    ),
  );
